<template>
  <el-dialog
    :visible="visible"
    :fullscreen="true"
    :close-on-click-modal="false"
    :show-close="false"
    custom-class="feedback-dialog"
    append-to-body
    @close="close"
  >
    <div class="feedback-page">
      <header class="feedback-page__header">
        <div>
          <h1>{{ $t("header.feedbackDialog.title") }}</h1>
        </div>
        <button
          type="button"
          class="feedback-page__close"
          :aria-label="$t('header.feedbackDialog.close')"
          @click="close"
        >
          <i class="el-icon-close"></i>
        </button>
      </header>

      <main class="feedback-page__content">
        <section class="feedback-entry" @click="openCreate">
          <span class="feedback-entry__icon"><i class="el-icon-service"></i></span>
          <span class="feedback-entry__copy">
            <strong>{{ $t("header.feedbackDialog.submitTitle") }}</strong>
            <small>{{ $t("header.feedbackDialog.submitDesc") }}</small>
          </span>
          <i class="el-icon-arrow-right feedback-entry__arrow"></i>
        </section>

        <section class="feedback-list-panel">
          <div class="feedback-list-panel__heading">
            <div>
              <h2>{{ $t("header.feedbackDialog.myFeedback") }}</h2>
              <p>{{ $t("header.feedbackDialog.listHint") }}</p>
            </div>
          </div>

          <div v-loading="feedbackLoading" class="feedback-list">
            <div v-if="!feedbackLoading && !drafts.length && feedbacks.length === 0" class="feedback-empty">
              {{ $t("header.feedbackDialog.empty") }}
            </div>
            <article v-for="draft in drafts" :key="draft.id" class="feedback-card feedback-card--draft">
              <div class="feedback-card__main">
                <span class="feedback-tag" :class="`feedback-tag--${String(draft.type).toLowerCase()}`">
                  {{ typeLabel(draft.type) }}
                </span>
                <h3>{{ $t("header.feedbackDialog.draftTitle", { type: typeLabel(draft.type), title: draft.title || moduleLabel(draft.module) }) }}</h3>
                <p>{{ draftSummary(draft) }}</p>
              </div>
              <div class="feedback-card__meta">
                <strong class="status--draft">{{ $t("header.feedbackDialog.draftStatus") }}</strong>
                <span>{{ draft.id }}</span>
                <span>{{ $t("header.feedbackDialog.justNow") }}</span>
                <div class="feedback-card__actions">
                  <button type="button" class="feedback-card__reply" @click="editDraft(draft)">
                    <i class="el-icon-edit-outline"></i>
                    {{ $t("header.feedbackDialog.continueEdit") }}
                  </button>
                  <button
                    type="button"
                    class="feedback-card__delete"
                    :aria-label="$t('header.feedbackDialog.deleteDraft')"
                    @click="deleteDraft(draft)"
                  >
                    <i class="el-icon-delete"></i>
                  </button>
                </div>
              </div>
            </article>
            <article v-for="item in feedbacks" :key="item.id" class="feedback-card">
              <div class="feedback-card__main">
                <span class="feedback-tag" :class="`feedback-tag--${String(item.type).toLowerCase()}`">
                  {{ typeLabel(item.type) }}
                </span>
                <h3>{{ item.title }}</h3>
                <p>{{ item.description }}</p>
              </div>
              <div class="feedback-card__meta">
                <strong :class="`status--${String(item.status).toLowerCase()}`">{{ statusLabel(item.status) }}</strong>
                <span>{{ item.id }}</span>
                <span>{{ item.time }}</span>
                <div class="feedback-card__actions">
                  <button type="button" class="feedback-card__action" @click="openDetail(item)">
                    {{ $t("header.feedbackDialog.view") }}
                  </button>
                  <button
                    v-if="item.reply || ['RESOLVED', 'CLOSED'].includes(String(item.status).toUpperCase())"
                    type="button"
                    class="feedback-card__reply"
                    @click="openReply(item)"
                  >
                    {{ $t("header.feedbackDialog.platformReply") }}
                    <span v-if="Number(item.unreadReplyCount) > 0" class="feedback-unread-count">{{ item.unreadReplyCount }}</span>
                  </button>
                  <button
                    type="button"
                    class="feedback-card__delete"
                    :aria-label="$t('header.feedbackDialog.delete')"
                    @click="openDelete(item)"
                  >
                    <i class="el-icon-delete"></i>
                  </button>
                </div>
              </div>
            </article>
          </div>
        </section>
      </main>

      <div v-if="dialogMode !== 'list'" class="feedback-overlay" @click.self="closeMode">
        <section v-if="dialogMode === 'create'" class="feedback-modal feedback-modal--form">
          <div class="feedback-modal__header">
            <div>
              <h2>{{ $t("header.feedbackDialog.formTitle") }}</h2>
              <p>{{ $t("header.feedbackDialog.formHint") }}</p>
            </div>
            <button type="button" class="feedback-modal__close" @click="closeMode">
              <i class="el-icon-close"></i>
            </button>
          </div>
          <form @submit.prevent="submitFeedback">
            <label class="feedback-field feedback-field--full">
              <span>{{ $t("header.feedbackDialog.typeLabel") }}</span>
              <select v-model="form.type" :disabled="optionsLoading">
                <option v-for="option in typeOptions" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
              <small class="feedback-field__hint">{{ typeHint }}</small>
            </label>
            <div class="feedback-form-grid">
              <label class="feedback-field">
                <span>{{ $t("header.feedbackDialog.titleLabel") }} <em>*</em></span>
                <div class="feedback-input-wrap" :class="{ 'is-error': fieldInvalid('title') }">
                  <input
                    ref="title"
                    v-model.trim="form.title"
                    maxlength="50"
                    :aria-invalid="fieldInvalid('title')"
                    :placeholder="$t('header.feedbackDialog.titlePlaceholder')"
                    @input="clearFieldError('title')"
                  />
                  <small>{{ form.title.length }}/50</small>
                </div>
                <span v-if="fieldInvalid('title')" class="feedback-field__error" role="alert">
                  {{ requiredFieldMessage("titleLabel") }}
                </span>
              </label>
              <label class="feedback-field">
                <span>{{ $t("header.feedbackDialog.moduleLabel") }}</span>
                <select v-model="form.module" :disabled="optionsLoading">
                  <option v-for="option in moduleOptions" :key="option.value" :value="option.value">
                    {{ option.label }}
                  </option>
                </select>
              </label>
            </div>
            <label v-if="form.type === 'BUG'" class="feedback-field feedback-field--full">
              <span>{{ $t("header.feedbackDialog.descriptionLabel") }} <em>*</em></span>
              <div class="feedback-input-wrap feedback-input-wrap--textarea" :class="{ 'is-error': fieldInvalid('description') }">
                <textarea
                  ref="description"
                  v-model.trim="form.description"
                  maxlength="200"
                  :aria-invalid="fieldInvalid('description')"
                  rows="3"
                  :placeholder="$t('header.feedbackDialog.descriptionPlaceholder')"
                  @input="clearFieldError('description')"
                ></textarea>
                <small>{{ form.description.length }}/200</small>
              </div>
              <span v-if="fieldInvalid('description')" class="feedback-field__error" role="alert">
                {{ requiredFieldMessage("descriptionLabel") }}
              </span>
            </label>
            <label v-if="form.type === 'BUG'" class="feedback-field feedback-field--full">
              <span>{{ $t("header.feedbackDialog.stepsLabel") }}</span>
              <div class="feedback-input-wrap feedback-input-wrap--textarea">
                <textarea
                  v-model.trim="form.steps"
                  maxlength="200"
                  rows="3"
                  :placeholder="$t('header.feedbackDialog.stepsPlaceholder')"
                ></textarea>
                <small>{{ form.steps.length }}/200</small>
              </div>
            </label>
            <label v-if="form.type === 'OPTIMIZATION'" class="feedback-field feedback-field--full">
              <span>{{ $t("header.feedbackDialog.improvementLabel") }} <em>*</em></span>
              <div class="feedback-input-wrap feedback-input-wrap--textarea" :class="{ 'is-error': fieldInvalid('improvementSuggestion') }">
                <textarea
                  ref="improvementSuggestion"
                  v-model.trim="form.improvementSuggestion"
                  maxlength="200"
                  :aria-invalid="fieldInvalid('improvementSuggestion')"
                  rows="3"
                  :placeholder="$t('header.feedbackDialog.improvementPlaceholder')"
                  @input="clearFieldError('improvementSuggestion')"
                ></textarea>
                <small>{{ form.improvementSuggestion.length }}/200</small>
              </div>
              <span v-if="fieldInvalid('improvementSuggestion')" class="feedback-field__error" role="alert">
                {{ requiredFieldMessage("improvementLabel") }}
              </span>
            </label>
            <label v-if="form.type === 'OPTIMIZATION'" class="feedback-field feedback-field--full">
              <span>{{ $t("header.feedbackDialog.impactLabel") }}</span>
              <div class="feedback-input-wrap feedback-input-wrap--textarea">
                <textarea
                  v-model.trim="form.usageScenario"
                  maxlength="200"
                  rows="3"
                  :placeholder="$t('header.feedbackDialog.impactPlaceholder')"
                ></textarea>
                <small>{{ form.usageScenario.length }}/200</small>
              </div>
            </label>
            <template v-if="form.type === 'REQUIREMENT'">
              <label class="feedback-field feedback-field--full">
                <span>{{ $t("header.feedbackDialog.scenarioLabel") }} <em>*</em></span>
                <div class="feedback-input-wrap feedback-input-wrap--textarea" :class="{ 'is-error': fieldInvalid('usageScenario') }">
                  <textarea
                    ref="usageScenario"
                    v-model.trim="form.usageScenario"
                    maxlength="200"
                    :aria-invalid="fieldInvalid('usageScenario')"
                    rows="3"
                    :placeholder="$t('header.feedbackDialog.scenarioPlaceholder')"
                    @input="clearFieldError('usageScenario')"
                  ></textarea>
                  <small>{{ form.usageScenario.length }}/200</small>
                </div>
                <span v-if="fieldInvalid('usageScenario')" class="feedback-field__error" role="alert">
                  {{ requiredFieldMessage("scenarioLabel") }}
                </span>
              </label>
              <label class="feedback-field feedback-field--full">
                <span>{{ $t("header.feedbackDialog.goalLabel") }} <em>*</em></span>
                <div class="feedback-input-wrap feedback-input-wrap--textarea" :class="{ 'is-error': fieldInvalid('requirementGoal') }">
                  <textarea
                    ref="requirementGoal"
                    v-model.trim="form.requirementGoal"
                    maxlength="200"
                    :aria-invalid="fieldInvalid('requirementGoal')"
                    rows="3"
                    :placeholder="$t('header.feedbackDialog.goalPlaceholder')"
                    @input="clearFieldError('requirementGoal')"
                  ></textarea>
                  <small>{{ form.requirementGoal.length }}/200</small>
                </div>
                <span v-if="fieldInvalid('requirementGoal')" class="feedback-field__error" role="alert">
                  {{ requiredFieldMessage("goalLabel") }}
                </span>
              </label>
              <label class="feedback-field feedback-field--full">
                <span>{{ $t("header.feedbackDialog.valueLabel") }}</span>
                <div class="feedback-input-wrap feedback-input-wrap--textarea">
                  <textarea
                    v-model.trim="form.requirementValue"
                    maxlength="200"
                    rows="3"
                    :placeholder="$t('header.feedbackDialog.valuePlaceholder')"
                  ></textarea>
                  <small>{{ form.requirementValue.length }}/200</small>
                </div>
              </label>
            </template>
            <div class="feedback-upload-row">
              <label class="feedback-upload">
                <i class="el-icon-paperclip"></i>
                {{ $t("header.feedbackDialog.upload") }}
                <input type="file" accept=".png,.jpg,.jpeg,.mp4" multiple @change="handleFiles" />
              </label>
              <span>{{ $t("header.feedbackDialog.uploadHint") }}</span>
            </div>
            <div v-if="form.files.length" class="feedback-file-list">
              <div
                v-for="file in form.files"
                :key="file.uid"
                class="feedback-file"
                :class="{ 'feedback-file--clickable': file.previewUrl }"
                @click="previewFileItem(file)"
              >
                <img
                  v-if="file.contentType.indexOf('image/') === 0 && file.previewUrl"
                  class="feedback-file__thumb"
                  :src="file.previewUrl"
                  :alt="file.fileName"
                />
                <video
                  v-else-if="file.contentType.indexOf('video/') === 0 && file.previewUrl"
                  class="feedback-file__thumb"
                  :src="file.previewUrl"
                  muted
                  preload="metadata"
                ></video>
                <i v-else :class="file.contentType.indexOf('video/') === 0 ? 'el-icon-video-camera' : 'el-icon-picture-outline'"></i>
                <span class="feedback-file__name">{{ file.fileName }}</span>
                <span v-if="file.status === 'uploading'" class="feedback-file__status">{{ $t("header.feedbackDialog.uploading") }}</span>
                <span v-else-if="file.status === 'error'" class="feedback-file__status feedback-file__status--error">{{ $t("header.feedbackDialog.uploadFailed") }}</span>
                <span v-else class="feedback-file__status feedback-file__status--success">{{ $t("header.feedbackDialog.uploaded") }}</span>
                <button type="button" class="feedback-file__remove" :aria-label="$t('header.feedbackDialog.removeFile')" @click.stop="removeFile(file)">
                  <i class="el-icon-close"></i>
                </button>
              </div>
            </div>
            <div v-if="previewFile" class="feedback-preview" @click.self="previewFile = null">
              <button type="button" class="feedback-preview__close" @click="previewFile = null"><i class="el-icon-close"></i></button>
              <img v-if="previewFile.contentType.indexOf('image/') === 0" :src="previewFile.previewUrl" :alt="previewFile.fileName" />
              <video v-else :src="previewFile.previewUrl" controls></video>
            </div>
            <div class="feedback-submit-actions">
              <button type="button" class="feedback-submit feedback-submit--draft" :disabled="hasUploadingFiles" @click="saveDraft">
                <i class="el-icon-document"></i>
                {{ $t("header.feedbackDialog.saveDraft") }}
              </button>
              <button type="submit" class="feedback-submit" :disabled="submitting || hasUploadingFiles">
                <i class="el-icon-position"></i>
                {{ submitting ? $t("header.feedbackDialog.submitting") : $t("header.feedbackDialog.submit") }}
              </button>
            </div>
          </form>
        </section>

        <section v-else-if="dialogMode === 'detail'" v-loading="detailLoading" class="feedback-modal feedback-modal--detail">
          <div class="feedback-modal__header">
            <div>
              <h2>{{ $t("header.feedbackDialog.detailTitle") }}</h2>
              <p>{{ $t("header.feedbackDialog.readonly") }}</p>
            </div>
            <button type="button" class="feedback-modal__close" @click="closeMode">
              <i class="el-icon-close"></i>
            </button>
          </div>
          <div class="feedback-detail-status">
            <span class="feedback-tag" :class="`feedback-tag--${String(activeFeedback.type).toLowerCase()}`">{{ typeLabel(activeFeedback.type) }}</span>
            <strong :class="`status--${String(activeFeedback.status).toLowerCase()}`">{{ statusLabel(activeFeedback.status) }}</strong>
            <span>{{ activeFeedback.time }}</span>
          </div>
          <div class="feedback-detail-grid">
            <div><span>{{ $t("header.feedbackDialog.ticket") }}</span><strong>{{ activeFeedback.id }}</strong></div>
            <div><span>{{ $t("header.feedbackDialog.moduleLabel") }}</span><strong>{{ moduleLabel(activeFeedback.module) }}</strong></div>
          </div>
          <div class="feedback-detail-block"><span>{{ $t("header.feedbackDialog.titleLabel") }}</span><strong>{{ activeFeedback.title }}</strong></div>
          <template v-if="String(activeFeedback.type).toUpperCase() === 'BUG'">
            <div class="feedback-detail-block"><span>{{ $t("header.feedbackDialog.descriptionLabel") }}</span><strong>{{ activeFeedback.description || $t("header.feedbackDialog.notProvided") }}</strong></div>
            <div class="feedback-detail-block"><span>{{ $t("header.feedbackDialog.stepsLabel") }}</span><strong>{{ activeFeedback.steps || $t("header.feedbackDialog.notProvided") }}</strong></div>
          </template>
          <template v-else-if="String(activeFeedback.type).toUpperCase() === 'OPTIMIZATION'">
            <div class="feedback-detail-block"><span>{{ $t("header.feedbackDialog.improvementLabel") }}</span><strong>{{ activeFeedback.improvementSuggestion || $t("header.feedbackDialog.notProvided") }}</strong></div>
            <div class="feedback-detail-block"><span>{{ $t("header.feedbackDialog.impactLabel") }}</span><strong>{{ activeFeedback.usageScenario || $t("header.feedbackDialog.notProvided") }}</strong></div>
          </template>
          <template v-else>
            <div class="feedback-detail-block"><span>{{ $t("header.feedbackDialog.scenarioLabel") }}</span><strong>{{ activeFeedback.usageScenario || $t("header.feedbackDialog.notProvided") }}</strong></div>
            <div class="feedback-detail-block"><span>{{ $t("header.feedbackDialog.goalLabel") }}</span><strong>{{ activeFeedback.requirementGoal || $t("header.feedbackDialog.notProvided") }}</strong></div>
            <div class="feedback-detail-block"><span>{{ $t("header.feedbackDialog.valueLabel") }}</span><strong>{{ activeFeedback.requirementValue || $t("header.feedbackDialog.notProvided") }}</strong></div>
          </template>
          <div v-if="activeFeedback.attachments && activeFeedback.attachments.length" class="feedback-reply-attachments">
            <div class="feedback-reply-attachments__header">
              <strong>{{ $t("header.feedbackDialog.attachments") }}</strong>
              <span>{{ $t("header.feedbackDialog.attachmentCount", { count: activeFeedback.attachments.length }) }}</span>
            </div>
            <div class="feedback-reply-attachments__grid">
              <div
                v-for="(attachment, index) in activeFeedback.attachments"
                :key="attachment.id || attachment.objectKey || attachment.fileName || index"
                class="feedback-reply-attachment"
                :class="{ 'feedback-reply-attachment--clickable': canPreviewAttachment(attachment) }"
                @click="openReplyAttachment(attachment)"
              >
                <div class="feedback-reply-attachment__preview" :class="attachmentPreviewClass(attachment)">
                  <img
                    v-if="isImageAttachment(attachment) && attachmentUrl(attachment)"
                    :src="attachmentUrl(attachment)"
                    :alt="attachmentName(attachment)"
                  />
                  <video
                    v-else-if="isVideoAttachment(attachment) && attachmentUrl(attachment)"
                    :src="attachmentUrl(attachment)"
                    muted
                    preload="metadata"
                  ></video>
                  <template v-else>
                    <i class="el-icon-document"></i>
                    <strong>{{ attachmentExtension(attachment) }}</strong>
                  </template>
                </div>
                <strong class="feedback-reply-attachment__name">{{ attachmentName(attachment) }}</strong>
                <span class="feedback-reply-attachment__meta">{{ attachmentExtension(attachment) }}<template v-if="attachmentSize(attachment)"> · {{ attachmentSize(attachment) }}</template></span>
                <div class="feedback-reply-attachment__actions">
                  <button type="button" @click.stop="openReplyAttachment(attachment)">
                    {{ $t("header.feedbackDialog.view") }}
                  </button>
                  <button type="button" :aria-label="$t('header.feedbackDialog.download')" @click.stop="downloadReplyAttachment(attachment)"><i class="el-icon-download"></i></button>
                </div>
              </div>
            </div>
          </div>
          <button type="button" class="feedback-submit feedback-submit--secondary" @click="closeMode">{{ $t("header.feedbackDialog.close") }}</button>
        </section>

        <section v-else-if="dialogMode === 'delete'" class="feedback-modal feedback-modal--confirm">
          <h2>{{ $t("header.feedbackDialog.deleteTitle") }}</h2>
          <p>{{ $t("header.feedbackDialog.deleteMessage") }}</p>
          <div class="feedback-confirm-actions">
            <button type="button" class="feedback-cancel" @click="closeMode">{{ $t("header.feedbackDialog.cancel") }}</button>
            <button type="button" class="feedback-danger" :disabled="deleting" @click="deleteFeedback">{{ $t("header.feedbackDialog.confirmDelete") }}</button>
          </div>
        </section>

        <section v-else v-loading="detailLoading" class="feedback-modal feedback-modal--reply">
          <div class="feedback-modal__header">
            <div><h2>{{ $t("header.feedbackDialog.replyTitle") }}</h2><p>{{ activeFeedback.id }}</p></div>
            <button type="button" class="feedback-modal__close" @click="closeMode"><i class="el-icon-close"></i></button>
          </div>
          <div class="feedback-detail-status">
            <span class="feedback-tag" :class="`feedback-tag--${String(activeFeedback.type).toLowerCase()}`">{{ typeLabel(activeFeedback.type) }}</span>
            <strong class="status--resolved">{{ statusLabel(activeFeedback.status) }}</strong>
            <span>{{ activeFeedback.time }}</span>
          </div>
          <p class="feedback-reply-title">{{ activeFeedback.title }}</p>
          <div class="feedback-reply-box"><span>{{ $t("header.feedbackDialog.replyContent") }}</span>{{ activeFeedback.reply || $t("header.feedbackDialog.notProvided") }}</div>
          <div v-if="activeFeedback.replyAttachments && activeFeedback.replyAttachments.length" class="feedback-reply-attachments">
            <div class="feedback-reply-attachments__header">
              <strong>{{ $t("header.feedbackDialog.attachments") }}</strong>
              <span>{{ $t("header.feedbackDialog.attachmentCount", { count: activeFeedback.replyAttachments.length }) }}</span>
            </div>
            <div class="feedback-reply-attachments__grid">
              <div
                v-for="(attachment, index) in activeFeedback.replyAttachments"
                :key="attachment.id || attachment.objectKey || attachment.fileName || index"
                class="feedback-reply-attachment"
                :class="{ 'feedback-reply-attachment--clickable': canPreviewAttachment(attachment) }"
                @click="openReplyAttachment(attachment)"
              >
                <div class="feedback-reply-attachment__preview" :class="attachmentPreviewClass(attachment)">
                  <img
                    v-if="isImageAttachment(attachment) && attachmentUrl(attachment)"
                    :src="attachmentUrl(attachment)"
                    :alt="attachmentName(attachment)"
                  />
                  <video
                    v-else-if="isVideoAttachment(attachment) && attachmentUrl(attachment)"
                    :src="attachmentUrl(attachment)"
                    muted
                    preload="metadata"
                  ></video>
                  <template v-else>
                    <i class="el-icon-document"></i>
                    <strong>{{ attachmentExtension(attachment) }}</strong>
                  </template>
                </div>
                <strong class="feedback-reply-attachment__name">{{ attachmentName(attachment) }}</strong>
                <span class="feedback-reply-attachment__meta">{{ attachmentExtension(attachment) }}<template v-if="attachmentSize(attachment)"> · {{ attachmentSize(attachment) }}</template></span>
                <div class="feedback-reply-attachment__actions">
                  <button type="button" @click.stop="openReplyAttachment(attachment)">
                    {{ $t("header.feedbackDialog.view") }}
                  </button>
                  <button type="button" :aria-label="$t('header.feedbackDialog.download')" @click.stop="downloadReplyAttachment(attachment)"><i class="el-icon-download"></i></button>
                </div>
              </div>
            </div>
          </div>
          <button type="button" class="feedback-submit feedback-submit--secondary" :disabled="readingReply" @click="markReplyRead">{{ $t("header.feedbackDialog.gotIt") }}</button>
        </section>
        <div v-if="replyPreview" class="feedback-preview" @click.self="replyPreview = null">
          <button type="button" class="feedback-preview__close" @click="replyPreview = null"><i class="el-icon-close"></i></button>
          <img v-if="replyPreview.type !== 'video'" :src="replyPreview.url" :alt="replyPreview.name" />
          <video v-else :src="replyPreview.url" controls></video>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script>
const emptyForm = () => ({
  type: "BUG",
  module: "RENDER_EXPORT",
  title: "",
  description: "",
  steps: "",
  improvementSuggestion: "",
  usageScenario: "",
  requirementGoal: "",
  requirementValue: "",
  files: [],
});
const MAX_UPLOAD_SIZE = 200 * 1024 * 1024;
const ALLOWED_UPLOAD_TYPES = { ".png": "image/png", ".jpg": "image/jpeg", ".jpeg": "image/jpeg", ".mp4": "video/mp4" };
const FEEDBACK_DRAFT_STORAGE_KEY = "manyu.feedback.draft";

export default {
  name: "FeedbackDialog",
  props: { visible: { type: Boolean, default: false } },
  data() {
    return {
      dialogMode: "list",
      activeFeedback: null,
      form: emptyForm(),
      invalidFields: [],
      typeOptions: [],
      moduleOptions: [],
      statusOptions: [],
      optionsLoading: false,
      optionsLoaded: false,
      feedbackLoading: false,
      detailLoading: false,
      submitting: false,
      deleting: false,
      readingReply: false,
      feedbackOpened: false,
      previewFile: null,
      replyPreview: null,
      feedbacks: [],
      drafts: [],
      editingDraftId: null,
    };
  },
  computed: {
    hasUploadingFiles() {
      return this.form.files.some((file) => file.status === "uploading");
    },
    typeHint() {
      const key = this.form.type === "OPTIMIZATION"
        ? "optimizationHint"
        : this.form.type === "REQUIREMENT"
          ? "featureHint"
          : "bugHint";
      return this.$t(`header.feedbackDialog.${key}`);
    },
  },
  created() {
    this.setFallbackOptions();
    this.loadDraft();
    window.addEventListener("auth-token-changed", this.handleAuthTokenChanged);
  },
  beforeDestroy() {
    this.form.files.forEach((file) => file.previewUrl && URL.revokeObjectURL(file.previewUrl));
    window.removeEventListener("auth-token-changed", this.handleAuthTokenChanged);
  },
  watch: {
    visible(value) {
      if (value) {
        if (this.feedbackOpened) return;
        this.feedbackOpened = true;
        this.loadDraft();
        this.loadOptions();
        this.loadFeedbacks();
      } else {
        this.feedbackOpened = false;
      }
    },
  },
  methods: {
    handleAuthTokenChanged() {
      if (!window.localStorage.getItem("token")) this.clearDraft();
    },
    loadDraft() {
      try {
        const raw = window.localStorage.getItem(FEEDBACK_DRAFT_STORAGE_KEY);
        const stored = raw ? JSON.parse(raw) : [];
        this.drafts = Array.isArray(stored)
          ? stored.filter((draft) => draft && typeof draft === "object")
          : stored && typeof stored === "object"
            ? [stored]
            : [];
      } catch (error) {
        this.drafts = [];
      }
    },
    clearDraft() {
      window.localStorage.removeItem(FEEDBACK_DRAFT_STORAGE_KEY);
      this.drafts = [];
      this.editingDraftId = null;
    },
    persistDrafts() {
      window.localStorage.setItem(
        FEEDBACK_DRAFT_STORAGE_KEY,
        JSON.stringify(this.drafts),
      );
    },
    draftSummary(draft) {
      return (
        draft.description ||
        draft.improvementSuggestion ||
        draft.usageScenario ||
        this.$t("header.feedbackDialog.draftEmpty")
      );
    },
    saveDraft() {
      const draft = {
        id: `DRAFT-${Date.now()}`,
        savedAt: Date.now(),
        type: this.form.type,
        module: this.form.module,
        title: this.form.title,
        description: this.form.description,
        steps: this.form.steps,
        improvementSuggestion: this.form.improvementSuggestion,
        usageScenario: this.form.usageScenario,
        requirementGoal: this.form.requirementGoal,
        requirementValue: this.form.requirementValue,
        files: this.form.files
          .filter((file) => file.status === "success" && file.attachment)
          .map((file) => ({
            uid: file.uid,
            fileName: file.fileName,
            contentType: file.contentType,
            status: "success",
            attachment: file.attachment,
          })),
      };
      const hasContent = [
        draft.title,
        draft.description,
        draft.steps,
        draft.improvementSuggestion,
        draft.usageScenario,
        draft.requirementGoal,
        draft.requirementValue,
      ].some((value) => String(value || "").trim());
      if (!hasContent) {
        this.$message.warning(this.$t("header.feedbackDialog.draftEmpty"));
        return;
      }
      try {
        const index = this.drafts.findIndex(
          (item) => item.id === this.editingDraftId,
        );
        if (index === -1) this.drafts.unshift(draft);
        else this.$set(this.drafts, index, { ...draft, id: this.editingDraftId });
        this.persistDrafts();
        this.$message.success(this.$t("header.feedbackDialog.draftSaved"));
        this.closeMode();
      } catch (error) {
        this.$message.error(this.$t("header.feedbackDialog.draftSaveFailed"));
      }
    },
    editDraft(draft) {
      if (!draft) return;
      this.editingDraftId = draft.id;
      this.form = {
        ...emptyForm(),
        ...draft,
        files: (Array.isArray(draft.files) ? draft.files : []).map(
          (file) => ({ ...file, file: null, previewUrl: null }),
        ),
      };
      this.dialogMode = "create";
    },
    deleteDraft(draft) {
      this.drafts = this.drafts.filter((item) => item.id !== draft.id);
      this.persistDrafts();
    },
    removeDraftById(id) {
      if (!id) return;
      this.drafts = this.drafts.filter((item) => item.id !== id);
      this.persistDrafts();
    },
    setFallbackOptions() {
      if (this.optionsLoaded) return;
      this.typeOptions = [
        { value: "BUG", label: this.$t("header.feedbackDialog.typeBug") },
        { value: "OPTIMIZATION", label: this.$t("header.feedbackDialog.typeOptimization") },
        { value: "REQUIREMENT", label: this.$t("header.feedbackDialog.typeFeature") },
      ];
      this.moduleOptions = [
        { value: "RENDER_EXPORT", label: this.$t("header.feedbackDialog.moduleCanvas") },
        { value: "AI_DRAWING", label: this.$t("header.feedbackDialog.moduleWorkflow") },
        { value: "DUBBING", label: this.$t("header.feedbackDialog.moduleAccount") },
      ];
      this.statusOptions = [
        { value: "PROCESSING", label: this.$t("header.feedbackDialog.statusProcessing") },
        { value: "RESOLVED", label: this.$t("header.feedbackDialog.statusResolved") },
      ];
    },
    normalizeOptionList(list) {
      return (Array.isArray(list) ? list : [])
        .map((item) => {
          if (typeof item === "string" || typeof item === "number") {
            return { value: String(item), label: String(item) };
          }
          if (!item || typeof item !== "object") return null;
          const value = item.value ?? item.code ?? item.id ?? item.key ?? item.dictValue;
          const label = item.label ?? item.name ?? item.text ?? item.description ?? value;
          return value === undefined || value === null || label === undefined || label === null
            ? null
            : { value: String(value), label: String(label) };
        })
        .filter(Boolean);
    },
    findOptionList(data, keys) {
      for (const key of keys) {
        if (Array.isArray(data && data[key])) return data[key];
      }
      return Array.isArray(data) ? data : [];
    },
    extractPlatformReply(source) {
      const activities = Array.isArray(source && source.activities) ? source.activities : [];
      const activity = [...activities].reverse().find((item) => item && item.replyContent);
      return (activity && activity.replyContent) || (source && (source.replyContent || source.platformReply || source.reply)) || "";
    },
    lastReplyActivity(source) {
      const activities = Array.isArray(source && source.activities) ? source.activities : [];
      return [...activities].reverse().find((item) => item && item.replyContent) || null;
    },
    extractReplyAttachments(source) {
      const activity = this.lastReplyActivity(source);
      if (Array.isArray(activity && activity.attachments) && activity.attachments.length) return activity.attachments;
      if (Array.isArray(source && source.replyAttachments) && source.replyAttachments.length) return source.replyAttachments;
      return Array.isArray(source && source.attachments) ? source.attachments : [];
    },
    extractUserAttachments(source) {
      const list = source && (source.attachments || source.userAttachments || source.files);
      if (Array.isArray(list) && list.length) return list;
      const activities = Array.isArray(source && source.activities) ? source.activities : [];
      const created = activities.find((item) => item && !item.replyContent && Array.isArray(item.attachments) && item.attachments.length);
      return created ? created.attachments : [];
    },
    attachmentUrl(attachment) {
      if (typeof attachment === "string") return this.normalizeAttachmentUrl(attachment);
      const file = attachment && (attachment.file || attachment.attachment || attachment);
      if (!file || typeof file !== "object") return "";
      const candidates = [
        file.url, file.fileUrl, file.downloadUrl, file.previewUrl, file.objectUrl,
        file.tosUrl, file.accessUrl, file.signedUrl, file.publicUrl, file.cdnUrl,
        file.thumbnailUrl, file.href, file.link, file.filePath, file.path,
      ];
      const raw = candidates.find((value) => typeof value === "string" && value.trim());
      return this.normalizeAttachmentUrl(raw);
    },
    normalizeAttachmentUrl(value) {
      const url = String(value || "").trim();
      if (!url || /^(blob:|data:|https?:\/\/|\/\/)/i.test(url)) return url;
      if (!url.startsWith("/")) return "";
      return `${String(this.$store.state.serverUrl || "").replace(/\/$/, "")}${url}`;
    },
    attachmentName(attachment) {
      const file = attachment && (attachment.file || attachment.attachment || attachment);
      return (file && (file.fileName || file.name || file.objectKey || file.fileUrl)) || "";
    },
    attachmentExtension(attachment) {
      const name = this.attachmentName(attachment);
      const file = attachment && (attachment.file || attachment.attachment || attachment);
      const contentType = String(file && (file.contentType || file.mimeType || file.type || ""));
      return (name.match(/\.([a-z0-9]+)(?:\?|$)/i) || [])[1]?.toUpperCase() || (contentType.split("/")[1] || "FILE").toUpperCase();
    },
    attachmentSize(attachment) {
      const file = attachment && (attachment.file || attachment.attachment || attachment);
      const bytes = Number(file && (file.fileSize || file.size || file.contentLength));
      if (!bytes) return "";
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
    isImageAttachment(attachment) {
      const file = attachment && (attachment.file || attachment.attachment || attachment);
      const type = String(file && (file.contentType || file.mimeType || file.type || file.fileType || "")).toLowerCase();
      const value = `${type} ${this.attachmentName(attachment)} ${this.attachmentUrl(attachment)}`.toLowerCase();
      return type === "image" || type.startsWith("image/") || /\.(png|jpe?g|gif|webp)(?:\?|$)/.test(value);
    },
    isVideoAttachment(attachment) {
      const file = attachment && (attachment.file || attachment.attachment || attachment);
      const type = String(file && (file.contentType || file.mimeType || file.type || file.fileType || "")).toLowerCase();
      const value = `${type} ${this.attachmentName(attachment)} ${this.attachmentUrl(attachment)}`.toLowerCase();
      return type === "video" || type.startsWith("video/") || /\.(mp4|webm|mov)(?:\?|$)/.test(value);
    },
    canPreviewAttachment(attachment) {
      return (this.isImageAttachment(attachment) || this.isVideoAttachment(attachment)) && !!this.attachmentUrl(attachment);
    },
    attachmentPreviewClass(attachment) {
      if (this.isImageAttachment(attachment) && this.attachmentUrl(attachment)) return "feedback-reply-attachment__preview--image";
      if (this.isVideoAttachment(attachment) && this.attachmentUrl(attachment)) return "feedback-reply-attachment__preview--video";
      return "feedback-reply-attachment__preview--file";
    },
    openReplyAttachment(attachment) {
      const url = this.attachmentUrl(attachment);
      if (!url) return;
      if (this.isImageAttachment(attachment)) this.replyPreview = { url, name: this.attachmentName(attachment), type: "image" };
      else if (this.isVideoAttachment(attachment)) this.replyPreview = { url, name: this.attachmentName(attachment), type: "video" };
      else window.open(url, "_blank", "noopener,noreferrer");
    },
    downloadReplyAttachment(attachment) {
      const url = this.attachmentUrl(attachment);
      if (!url) return;
      const link = document.createElement("a");
      link.href = url;
      link.download = this.attachmentName(attachment);
      link.target = "_blank";
      link.rel = "noopener";
      link.click();
    },
    applyOptionSelection(key, options) {
      if (!options.length) return;
      const current = String(this.form[key] || "");
      if (!options.some((option) => option.value === current)) {
        this.form[key] = options[0].value;
      }
    },
    async loadOptions() {
      if (this.optionsLoaded || this.optionsLoading) return;
      this.optionsLoading = true;
      try {
        const response = await this.$axios.get(
          `${this.$store.state.serverUrl}ytb/workOrder/options`,
        );
        const body = response && response.data;
        if (!body || (body.code !== undefined && Number(body.code) !== 200)) return;
        const data = body.data || body;
        const typeOptions = this.normalizeOptionList(
          this.findOptionList(data, ["types", "typeOptions", "typeList", "problemTypes", "problemType"]),
        );
        const statusOptions = this.normalizeOptionList(
          this.findOptionList(data, ["statuses", "statusOptions", "statusList", "status"]),
        );
        const moduleOptions = this.normalizeOptionList(
          this.findOptionList(data, ["moduleOptions", "modules", "moduleList", "workOrderModules", "businessModules", "module"]),
        );
        if (typeOptions.length) this.typeOptions = typeOptions;
        if (statusOptions.length) this.statusOptions = statusOptions;
        if (moduleOptions.length) this.moduleOptions = moduleOptions;
        this.applyOptionSelection("type", this.typeOptions);
        this.applyOptionSelection("module", this.moduleOptions);
      } catch (error) {
        // Keep the local options when the API is unavailable.
      } finally {
        this.optionsLoading = false;
        this.optionsLoaded = true;
      }
    },
    async loadFeedbacks() {
      if (this.feedbackLoading) return;
      this.feedbackLoading = true;
      try {
        const response = await this.$axios.get(
          `${this.$store.state.serverUrl}ytb/workOrder/my/page`,
          { params: { pageNum: 1, pageSize: 50 } },
        );
        const body = response && response.data;
        if (!body || (body.code !== undefined && Number(body.code) !== 200)) return;
        const data = body.data || body;
        const list = Array.isArray(data) ? data : Array.isArray(data.list) ? data.list : [];
        this.feedbacks = list.map((item) => ({
          ...item,
          workOrderId: item.id || item.workOrderId,
          id: item.workOrderNo || item.id,
          type: item.workOrderType || item.type || "BUG",
          status: item.status || "PROCESSING",
          module: item.moduleCode || item.module || "",
          time: item.createTime || item.updateTime || "",
          title: item.title || "",
          description: item.description || item.content || "",
          steps: item.steps || item.reproduceSteps || "",
          improvementSuggestion: item.improvementSuggestion || "",
          usageScenario: item.usageScenario || "",
          requirementGoal: item.requirementGoal || "",
          requirementValue: item.requirementValue || "",
          reply: this.extractPlatformReply(item),
        }));
      } catch (error) {
        // Keep the local fallback when the API is unavailable.
      } finally {
        this.feedbackLoading = false;
      }
    },
    close() {
      this.closeMode();
      this.$emit("update:visible", false);
    },
    closeMode() {
      this.dialogMode = "list";
      this.activeFeedback = null;
      this.previewFile = null;
      this.replyPreview = null;
      this.form.files.forEach((file) => file.previewUrl && URL.revokeObjectURL(file.previewUrl));
      this.form = emptyForm();
      this.invalidFields = [];
      this.editingDraftId = null;
    },
    openCreate() {
      this.form = emptyForm();
      this.invalidFields = [];
      this.editingDraftId = null;
      this.dialogMode = "create";
    },
    openDetail(item) {
      this.activeFeedback = item;
      this.dialogMode = "detail";
      this.loadFeedbackDetail(item);
    },
    async loadFeedbackDetail(item) {
      const workOrderId = item && (item.workOrderId || item.id);
      if (!workOrderId) return;
      this.detailLoading = true;
      try {
        const response = await this.$axios.get(
          `${this.$store.state.serverUrl}ytb/workOrder/my/detail`,
          { params: { workOrderId } },
        );
        const data = this.resolveApiData(response, this.$t("header.feedbackDialog.submitFailed"));
        const replyActivity = this.lastReplyActivity(data);
        this.activeFeedback = {
          ...item,
          ...data,
          workOrderId,
          id: data.workOrderNo || item.id,
          type: data.workOrderType || item.type,
          status: data.status || item.status,
          module: data.moduleCode || item.module,
          time: data.createTime || data.updateTime || item.time,
          title: data.title || item.title,
          description: data.problemDescription || data.description || data.content || item.description,
          steps: data.reproductionSteps || data.steps || item.steps,
          improvementSuggestion: data.improvementSuggestion || item.improvementSuggestion || "",
          usageScenario: data.usageScenario || item.usageScenario || "",
          requirementGoal: data.requirementGoal || item.requirementGoal || "",
          requirementValue: data.requirementValue || item.requirementValue || "",
          reply: (replyActivity && replyActivity.replyContent) || data.replyContent || data.platformReply || item.reply,
          unreadReplyCount: data.unreadReplyCount ?? item.unreadReplyCount,
          attachments: this.extractUserAttachments(data),
          replyAttachments: this.extractReplyAttachments(data),
        };
      } catch (error) {
        // Keep the list summary when the detail endpoint is unavailable.
      } finally {
        this.detailLoading = false;
      }
    },
    openDelete(item) { this.activeFeedback = item; this.dialogMode = "delete"; },
    openReply(item) {
      this.dialogMode = "reply";
      this.activeFeedback = item;
      this.loadFeedbackDetail(item);
    },
    async markReplyRead() {
      if (!this.activeFeedback || this.readingReply) return;
      this.readingReply = true;
      const workOrderId = this.activeFeedback.workOrderId;
      try {
        this.resolveApiData(
          await this.$axios.post(
            `${this.$store.state.serverUrl}ytb/workOrder/message/read`,
            { workOrderId },
          ),
          this.$t("header.feedbackDialog.submitFailed"),
        );
        await this.loadFeedbacks();
      } catch (error) {
        // The reply can still be closed when the read acknowledgement fails.
      } finally {
        this.readingReply = false;
        this.closeMode();
      }
    },
    async handleFiles(event) {
      const files = Array.from(event.target.files || []);
      event.target.value = "";
      if (this.form.files.length + files.length > 5) {
        this.$message.error(this.$t("header.feedbackDialog.fileCountInvalid"));
        return;
      }
      for (const file of files) {
        const extension = this.getFileExtension(file.name);
        if (ALLOWED_UPLOAD_TYPES[extension] !== file.type || file.size <= 0 || file.size > MAX_UPLOAD_SIZE) {
          this.$message.error(file.size > MAX_UPLOAD_SIZE ? this.$t("header.feedbackDialog.fileSizeInvalid") : this.$t("header.feedbackDialog.fileTypeInvalid"));
          continue;
        }
        const item = {
          uid: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          file,
          fileName: file.name,
          contentType: file.type,
          previewUrl: URL.createObjectURL(file),
          status: "uploading",
          attachment: null,
        };
        this.form.files.push(item);
        try {
          item.attachment = await this.uploadAttachment(file);
          item.status = "success";
        } catch (error) {
          item.status = "error";
        }
      }
    },
    previewFileItem(file) {
      if (file.previewUrl && file.status === "success") this.previewFile = file;
    },
    removeFile(file) {
      if (file.previewUrl) URL.revokeObjectURL(file.previewUrl);
      this.form.files = this.form.files.filter((item) => item.uid !== file.uid);
      if (this.previewFile && this.previewFile.uid === file.uid) this.previewFile = null;
    },
    resolveApiData(response, fallback) {
      const body = response && response.data;
      if (!body || (body.code !== undefined && Number(body.code) !== 200)) {
        throw new Error((body && body.message) || fallback);
      }
      return body.data !== undefined ? body.data : body;
    },
    resolveTosEndpoint(credential) {
      const rawEndpoint = String(credential.endpoint || "").trim();
      const endpoint = rawEndpoint.replace(/^https?:\/\//i, "").replace(/\/.*$/, "");
      const bucket = String(credential.bucket || "");
      const prefix = bucket ? `${bucket}.` : "";
      return prefix && endpoint.startsWith(prefix)
        ? { endpoint: endpoint.slice(prefix.length) }
        : endpoint ? { endpoint } : {};
    },
    async createTosClient(credential) {
      const { TOS } = await import("@volcengine/tos-sdk");
      const endpointValue = String(credential.endpoint || "");
      return new TOS({
        accessKeyId: credential.accessKeyId,
        accessKeySecret: credential.accessKeySecret,
        stsToken: credential.stsToken,
        region: credential.region,
        secure: !endpointValue.startsWith("http://"),
        ...this.resolveTosEndpoint(credential),
      });
    },
    getFileExtension(name) {
      const match = String(name || "").match(/\.[^.]+$/);
      return match ? match[0].toLowerCase() : "";
    },
    getTosFileType(contentType) {
      if (String(contentType).startsWith("image/")) return "image";
      if (String(contentType).startsWith("video/")) return "video";
      return "other";
    },
    createObjectKeyId() {
      if (typeof globalThis.crypto?.randomUUID === "function") return globalThis.crypto.randomUUID();
      return `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    },
    createRequestId() {
      return Date.now() * 1000 + Math.floor(Math.random() * 1000);
    },
    async uploadAttachment(file) {
      const credential = this.resolveApiData(
        await this.$axios.post(`${this.$store.state.serverUrl}ytb/tos/sts/credential`, {}),
        this.$t("header.feedbackDialog.uploadFailed"),
      );
      const client = await this.createTosClient(credential);
      const keyPrefix = String(credential.keyPrefix || "").replace(/^\/+/, "");
      const prefix = keyPrefix && !keyPrefix.endsWith("/") ? `${keyPrefix}/` : keyPrefix;
      const objectKey = `${prefix}${this.createObjectKeyId()}${this.getFileExtension(file.name)}`;
      await client.putObject({
        bucket: credential.bucket,
        key: objectKey,
        body: file,
        contentLength: file.size,
        contentType: file.type,
      });
      this.resolveApiData(
        await this.$axios.post(`${this.$store.state.serverUrl}ytb/tos/file/save`, {
          objectKey,
          fileName: file.name,
          fileType: this.getTosFileType(file.type),
          contentType: file.type,
          fileSize: file.size,
        }),
        this.$t("header.feedbackDialog.uploadFailed"),
      );
      return { objectKey, fileName: file.name, contentType: file.type, fileSize: file.size };
    },
    async submitFeedback() {
      const requiredFields = this.form.type === "BUG"
        ? ["title", "description"]
        : this.form.type === "OPTIMIZATION"
          ? ["title", "improvementSuggestion"]
          : ["title", "usageScenario", "requirementGoal"];
      this.invalidFields = requiredFields.filter(
        (field) => !String(this.form[field] || "").trim(),
      );
      if (this.invalidFields.length) {
        this.$nextTick(() => this.$refs[this.invalidFields[0]].focus());
        return;
      }
      this.submitting = true;
      try {
        if (this.form.files.some((file) => file.status === "uploading")) return;
        if (this.form.files.some((file) => file.status === "error")) {
          throw new Error(this.$t("header.feedbackDialog.uploadFailed"));
        }
        const attachments = this.form.files.map((file) => file.attachment).filter(Boolean);
        const payload = {
          requestId: this.createRequestId(),
          workOrderType: this.form.type,
          moduleCode: this.form.module,
          title: this.form.title,
          problemDescription: this.form.type === "BUG" ? this.form.description : undefined,
          reproductionSteps: this.form.type === "BUG" ? (this.form.steps || undefined) : undefined,
          painPoint: this.form.type === "OPTIMIZATION" ? this.form.title : undefined,
          improvementSuggestion: this.form.type === "OPTIMIZATION" ? this.form.improvementSuggestion : undefined,
          usageScenario: this.form.type === "OPTIMIZATION" || this.form.type === "REQUIREMENT" ? this.form.usageScenario : undefined,
          requirementGoal: this.form.type === "REQUIREMENT" ? this.form.requirementGoal : undefined,
          requirementValue: this.form.type === "REQUIREMENT" ? (this.form.requirementValue || undefined) : undefined,
          attachments: attachments.length ? attachments : undefined,
        };
        this.resolveApiData(
          await this.$axios.post(`${this.$store.state.serverUrl}ytb/workOrder/create`, payload),
          this.$t("header.feedbackDialog.submitFailed"),
        );
        this.$message.success(this.$t("header.feedbackDialog.submitSuccess"));
        this.removeDraftById(this.editingDraftId);
        this.closeMode();
        this.loadFeedbacks();
      } catch (error) {
        this.$message.error(error.message || this.$t("header.feedbackDialog.submitFailed"));
      } finally {
        this.submitting = false;
      }
    },
    fieldInvalid(field) {
      return this.invalidFields.includes(field);
    },
    clearFieldError(field) {
      this.invalidFields = this.invalidFields.filter((item) => item !== field);
    },
    requiredFieldMessage(labelKey) {
      return this.$t("header.feedbackDialog.requiredField", {
        field: this.$t(`header.feedbackDialog.${labelKey}`),
      });
    },
    async deleteFeedback() {
      const workOrderId = this.activeFeedback && (this.activeFeedback.workOrderId || this.activeFeedback.id);
      if (!workOrderId || this.deleting) return;
      this.deleting = true;
      try {
        this.resolveApiData(
          await this.$axios.post(`${this.$store.state.serverUrl}ytb/workOrder/my/hide`, { workOrderId }),
          this.$t("header.feedbackDialog.deleteFailed"),
        );
        this.$message.success(this.$t("header.feedbackDialog.deleteSuccess"));
        this.closeMode();
        await this.loadFeedbacks();
      } catch (error) {
        this.$message.error(error.message || this.$t("header.feedbackDialog.deleteFailed"));
      } finally {
        this.deleting = false;
      }
    },
    optionLabel(options, value, fallback) {
      const option = options.find((item) => String(item.value).toUpperCase() === String(value).toUpperCase());
      return option ? option.label : fallback;
    },
    typeLabel(type) {
      const fallback = this.$t(`header.feedbackDialog.type${String(type).toUpperCase() === "BUG" ? "Bug" : String(type).toUpperCase() === "OPTIMIZATION" ? "Optimization" : "Feature"}`);
      return this.optionLabel(this.typeOptions, type, fallback);
    },
    statusLabel(status) {
      const fallback = this.$t(`header.feedbackDialog.status${String(status).toUpperCase() === "PROCESSING" ? "Processing" : "Resolved"}`);
      return this.optionLabel(this.statusOptions, status, fallback);
    },
    moduleLabel(module) {
      const normalized = String(module).toUpperCase();
      const fallback = this.$t(`header.feedbackDialog.module${normalized === "RENDER_EXPORT" || normalized === "CANVAS" ? "Canvas" : normalized === "AI_DRAWING" || normalized === "WORKFLOW" ? "Workflow" : "Account"}`);
      return this.optionLabel(this.moduleOptions, module, fallback);
    },
  },
};
</script>

<style lang="less" scoped>
/deep/ .feedback-dialog { margin: 0 !important; width: 100% !important; height: 100vh; max-height: none; border-radius: 0; background: #08090f; box-shadow: none; .el-dialog__header { display: none; } .el-dialog__body { height: 100%; padding: 0; overflow: hidden; } }
.feedback-page { height: 100%; box-sizing: border-box; padding: 1.2rem 2.6vw 2.4rem; color: #eef0f7; background: radial-gradient(circle at 75% 0, rgba(92, 66, 182, .16), transparent 34rem), #08090f; overflow: auto; }
.feedback-page__header { display: flex; align-items: center; justify-content: space-between; min-height: 4.2rem; h1 { margin: .2rem 0 0; font-size: 1.7rem; line-height: 1.25; } }
.feedback-page__close, .feedback-modal__close { width: 2.45rem; height: 2.45rem; border: 1px solid #363b55; border-radius: 50%; background: #11131d; color: #edf0fb; cursor: pointer; font-size: 1.1rem; }
.feedback-page__content { max-width: 110rem; margin: 1rem auto 0; }
.feedback-entry, .feedback-list-panel { border: 1px solid #2b3048; border-radius: .8rem; background: #171821; }
.feedback-entry { min-height: 4.9rem; padding: 0 1.2rem; display: flex; align-items: center; gap: .85rem; cursor: pointer; &:hover { border-color: #8155ff; } }
.feedback-entry__icon { width: 2rem; color: #8c63ff; font-size: 1.4rem; }.feedback-entry__copy { display: flex; flex-direction: column; gap: .25rem; strong { font-size: 1rem; } small { color: #9ca5c2; } }.feedback-entry__arrow { margin-left: auto; color: #7c849f; }
.feedback-list-panel { margin-top: 1rem; padding: 1.2rem 1rem 1.4rem; }.feedback-list-panel__heading { display: flex; justify-content: space-between; align-items: center; gap: 1rem; margin: 0 .2rem 1rem; h2 { margin: 0; font-size: 1.2rem; } p { margin: .35rem 0 0; color: #8992ad; font-size: .82rem; } }
.feedback-list { display: grid; gap: .7rem; }.feedback-card { display: grid; grid-template-columns: minmax(0, 1fr) 11rem; min-height: 8rem; border: 1px solid #2a2f44; border-radius: .7rem; background: #11131d; }.feedback-card--draft { border-color: #6d4cc3; }.feedback-card__main { min-width: 0; padding: 1.05rem; }.feedback-tag { display: inline-flex; padding: .24rem .55rem; border-radius: .22rem; background: #edf7ff; color: #e63b3b; font-size: .76rem; }.feedback-tag--optimization { color: #06769c; }.feedback-tag--feature { color: #8060e9; }.feedback-card h3 { margin: .55rem 0 .25rem; font-size: 1rem; }.feedback-card p { margin: 0; color: #a0a8c0; font-size: .82rem; line-height: 1.5; }.feedback-card__meta { display: flex; flex-direction: column; align-items: flex-start; gap: .32rem; padding: 1rem .8rem; border-left: 1px solid #292e42; color: #929bb8; font-size: .76rem; }.feedback-card__meta strong { font-size: .95rem; }.status--processing { color: #35d4a0; }.status--resolved { color: #38d1a0; }.status--draft { color: #f7c948; }.feedback-card__actions { display: flex; align-items: center; flex-wrap: wrap; gap: .35rem; margin-top: auto; padding-top: .45rem; }.feedback-card__action, .feedback-card__reply { border: 1px solid #35405b; border-radius: 1rem; padding: .23rem .62rem; background: transparent; color: #dce3fa; cursor: pointer; }.feedback-card__reply { border-color: #7549d8; color: #c9b8ff; }.feedback-card__delete { flex: 0 0 1.9rem; width: 1.9rem; height: 1.9rem; padding: 0; border: 1px solid #63382e; border-radius: 50%; background: transparent; color: #a36e61; cursor: pointer; }
.feedback-empty { padding: 2rem 1rem; color: #929bb8; text-align: center; }
.feedback-overlay { position: fixed; z-index: 10; inset: 0; display: flex; align-items: center; justify-content: center; padding: 1.5rem; background: rgba(3, 4, 9, .74); }.feedback-modal { width: 54rem; max-width: calc(100vw - 2rem); max-height: calc(100vh - 2rem); overflow: auto; box-sizing: border-box; padding: 1.35rem 1.55rem; border: 1px solid #343951; border-radius: .85rem; background: #171821; box-shadow: 0 1.5rem 4rem rgba(0, 0, 0, .45); }.feedback-modal--form { width: 65rem; }.feedback-modal__header { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; padding-bottom: .9rem; border-bottom: 1px solid #2e3348; h2 { margin: 0; font-size: 1.35rem; } p { margin: .35rem 0 0; color: #98a1bd; font-size: .8rem; } }
.feedback-field__hint { display: block; margin-top: .45rem; color: #98a1bd; font-size: .75rem; line-height: 1.4; }
.feedback-field { display: block; min-width: 0; margin-top: 1rem; > span { display: block; margin-bottom: .45rem; color: #dfe4f4; font-size: .82rem; font-weight: 600; } > .feedback-field__error { margin: .4rem 0 0; color: #f05a57; font-weight: 400; } em { color: #ff654b; font-style: normal; } input, textarea, select { width: 100%; box-sizing: border-box; border: 1px solid #353b55; border-radius: .6rem; outline: none; background: #0f111a; color: #e6eafb; font: inherit; } input, select { height: 3rem; padding: 0 .85rem; } textarea { min-height: 5.4rem; padding: .75rem .85rem; resize: vertical; } input:focus, textarea:focus, select:focus { border-color: #8755ff; } select { appearance: auto; } }.feedback-input-wrap { position: relative; }.feedback-input-wrap.is-error input, .feedback-input-wrap.is-error textarea, .feedback-input-wrap.is-error input:focus, .feedback-input-wrap.is-error textarea:focus { border-color: #f05a57; }.feedback-input-wrap small { position: absolute; right: .75rem; bottom: .5rem; color: #76809e; font-size: .72rem; }.feedback-input-wrap input { padding-right: 3.2rem; }.feedback-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }.feedback-upload-row { display: flex; align-items: center; flex-wrap: wrap; gap: .75rem; margin-top: 1rem; color: #98a1bd; font-size: .78rem; }.feedback-upload { position: relative; display: inline-flex; align-items: center; gap: .4rem; padding: .65rem .85rem; border: 1px dashed #8253f1; border-radius: .55rem; color: #d6c8ff; cursor: pointer; input { position: absolute; inset: 0; width: 100%; opacity: 0; cursor: pointer; } }.feedback-upload__file { color: #c8baff; }.feedback-submit-actions { display: grid; grid-template-columns: 11rem minmax(0, 1fr); gap: .75rem; margin-top: 1.15rem; }.feedback-submit { width: 100%; height: 3.15rem; border: 0; border-radius: .7rem; background: linear-gradient(100deg, #7f48ff, #ff6942); color: #fff; font-size: 1rem; font-weight: 700; cursor: pointer; }.feedback-submit--draft { border: 1px solid #8253f1; background: #211b3c; color: #d6c8ff; }.feedback-submit--secondary { margin-top: 1.15rem; background: #824cff; }.feedback-detail-status { display: flex; align-items: center; gap: .7rem; margin: .9rem 0; color: #9aa4c0; font-size: .78rem; }.feedback-detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: .7rem; }.feedback-detail-grid > div, .feedback-detail-block, .feedback-reply-box { padding: .85rem; border: 1px solid #2e3449; border-radius: .65rem; background: #11131d; }.feedback-detail-grid span, .feedback-detail-block span, .feedback-reply-box span { display: block; margin-bottom: .45rem; color: #8f99b6; font-size: .78rem; }.feedback-detail-block { margin-top: .7rem; }.feedback-detail-block strong { line-height: 1.5; }.feedback-modal--confirm { width: 25rem; max-width: 100%; padding: 1.55rem; }.feedback-modal--confirm h2 { margin: 0 0 .75rem; font-size: 1.25rem; }.feedback-modal--confirm p { color: #b2b8cb; }.feedback-confirm-actions { display: flex; justify-content: flex-end; gap: .65rem; margin-top: 1.5rem; }.feedback-cancel, .feedback-danger { min-width: 4.4rem; padding: .55rem .85rem; border-radius: .55rem; cursor: pointer; font: inherit; font-weight: 600; }.feedback-cancel { border: 1px solid #3b435e; background: transparent; color: #e5e9f5; }.feedback-danger { border: 0; background: #ff6545; color: #fff; }.feedback-reply-title { margin: 1.2rem 0 .8rem; font-size: 1.1rem; font-weight: 700; }.feedback-reply-box { min-height: 5rem; color: #e1e6f2; line-height: 1.6; }
@media (max-width: 700px) { .feedback-page { padding: .8rem .75rem 1.5rem; }.feedback-page__header h1 { font-size: 1.45rem; }.feedback-list-panel__heading { align-items: flex-start; flex-direction: column; }.feedback-card { grid-template-columns: 1fr; }.feedback-card__meta { min-height: 7rem; border-top: 1px solid #292e42; border-left: 0; }.feedback-form-grid, .feedback-detail-grid { grid-template-columns: 1fr; }.feedback-submit-actions { grid-template-columns: 1fr; }.feedback-overlay { padding: .7rem; align-items: flex-start; overflow: auto; }.feedback-modal { max-height: calc(100vh - 1.4rem); padding: 1rem; }.feedback-page__close { flex: 0 0 auto; } }
.feedback-file-list { display: grid; gap: .4rem; margin-top: .65rem; }
.feedback-file { display: flex; align-items: center; gap: .5rem; min-height: 2.2rem; padding: .35rem .55rem; border: 1px solid #303750; border-radius: .45rem; background: #11131d; color: #cbd2e7; font-size: .78rem; }
.feedback-file__thumb { width: 1.7rem; height: 1.7rem; flex: 0 0 1.7rem; object-fit: cover; border-radius: .28rem; }
.feedback-file--clickable { cursor: pointer; }
.feedback-file__name { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.feedback-file__status { margin-left: auto; color: #a4abc0; }.feedback-file__status--success { color: #35d4a0; }.feedback-file__status--error { color: #ff765b; }
.feedback-file__remove { width: 1.6rem; height: 1.6rem; padding: 0; border: 0; background: transparent; color: #9ca5bd; cursor: pointer; }
.feedback-submit:disabled { opacity: .65; cursor: wait; }
.feedback-preview { position: fixed; z-index: 20; inset: 0; display: flex; align-items: center; justify-content: center; padding: 2rem; background: rgba(3, 4, 9, .88); }
.feedback-preview img, .feedback-preview video { width: 90vw; max-width: 70rem; max-height: 85vh; }
.feedback-preview__close { position: absolute; top: 1.2rem; right: 1.2rem; width: 2.3rem; height: 2.3rem; border: 1px solid #4a5270; border-radius: 50%; background: #171a27; color: #fff; cursor: pointer; }
.feedback-unread-count { display: inline-flex; align-items: center; justify-content: center; min-width: 1.2rem; height: 1.2rem; margin-left: .35rem; padding: 0 .25rem; border-radius: .7rem; background: #ff6545; color: #fff; font-size: .7rem; vertical-align: .15rem; }
.feedback-reply-attachments { margin-top: .8rem; padding: .7rem; border: 1px solid #2e3449; border-radius: .65rem; background: #11131d; }.feedback-reply-attachments__header { display: flex; justify-content: space-between; align-items: center; margin-bottom: .65rem; color: #a7b0ca; font-size: .75rem; }.feedback-reply-attachments__header strong { color: #dce3f7; }.feedback-reply-attachments__grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: .6rem; }.feedback-reply-attachment { display: flex; flex-direction: column; gap: .3rem; min-width: 0; padding: .45rem; border: 1px solid #2f3652; border-radius: .55rem; background: #171a27; color: #dfe5f6; font-size: .74rem; }.feedback-reply-attachment--clickable { cursor: zoom-in; }.feedback-reply-attachment__preview { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 5.4rem; overflow: hidden; border: 1px solid #493c7f; border-radius: .4rem; background: #1d1738; color: #bd9dff; }.feedback-reply-attachment__preview--file { background: #12332e; border-color: #276b5b; color: #4de1b0; }.feedback-reply-attachment__preview i { font-size: 1.7rem; }.feedback-reply-attachment__preview strong { margin-top: .2rem; font-size: .72rem; }.feedback-reply-attachment__preview img, .feedback-reply-attachment__preview video { display: block; width: 100%; height: 100%; object-fit: cover; background: #0b0d16; }.feedback-reply-attachment__name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }.feedback-reply-attachment__meta { color: #929bb8; font-size: .68rem; }.feedback-reply-attachment span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.feedback-reply-attachment__preview--file { background: #1d1738; border-color: #493c7f; color: #bd9dff; }.feedback-reply-attachment__preview--image, .feedback-reply-attachment__preview--video { padding: 0; border-color: #276b5b; background: #0b0d16; }.feedback-reply-attachment__actions { display: flex; align-items: center; gap: .35rem; margin-top: .15rem; }.feedback-reply-attachment__actions button, .feedback-reply-attachment__actions a { display: inline-flex; align-items: center; justify-content: center; gap: .25rem; height: 1.55rem; border: 1px solid #51418b; border-radius: .35rem; background: transparent; color: #d4c5ff; font-size: .68rem; text-decoration: none; cursor: pointer; }.feedback-reply-attachment__actions button { flex: 1; }.feedback-reply-attachment__actions a { width: 1.55rem; }.feedback-reply-attachment__actions button:disabled { opacity: .45; cursor: not-allowed; }
</style>



